import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Modal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { TextField } from '@/components/forms';
import type { User } from '@/features/auth';
import { formatPhoneUS } from '@/lib/utils/format';

import { CHANGE_PASSWORD_COPY, CODE_ENTRY_COPY } from '../../constants';
import {
  useProfile,
  useResendChangePasswordOtp,
  useSendChangePasswordOtp,
  useVerifyChangePasswordOtp,
} from '../../hooks';
import type { CodeDispatch } from '../../schemas';
import { OtpStepModal } from '../otp-step-modal/otp-step-modal';

import { NewPasswordStep } from './new-password-step';

export type ChangePasswordFlowProps = {
  user: User;
  /** Cancel / ✕ / mask / Escape on any step: nothing changed, no prompt (117548 BR 18). */
  onClose: () => void;
  /** The password was changed. */
  onDone: () => void;
  /** The code lapsed: nothing changed; back to the Profile panel (grilling Q2/Q13). */
  onExpired: () => void;
};

/**
 * The in-session Change Password flow (US-117548): send a code to the Registered phone number →
 * enter it → set a new password. Mounted by `<TopNav>` only while open, so closing it discards
 * every step's state. No current password is asked for, and no session ends.
 */
export function ChangePasswordFlow({ user, onClose, onDone, onExpired }: ChangePasswordFlowProps) {
  // The profile's identity wins: a fresh login's session user carries no phone (ticket 01).
  const { data } = useProfile();
  const phone = data?.identity.phone ?? user.phone ?? null;
  const [dispatch, setDispatch] = useState<CodeDispatch | null>(null);
  const [verified, setVerified] = useState(false);
  const send = useSendChangePasswordOtp();
  const verify = useVerifyChangePasswordOtp();
  const resend = useResendChangePasswordOtp();
  const toast = useToast();
  // One exit for the modal's timer / server codes and Step 3's 403 (spec: Change Password).
  const expire = useCallback(() => {
    toast.warning({ title: CODE_ENTRY_COPY.EXPIRED });
    onExpired();
  }, [toast, onExpired]);
  // Only so the read-only phone renders through <TextField>; `values` follows the profile load.
  const { control } = useForm({ values: { phone: phone ? formatPhoneUS(phone) : '' } });

  if (verified) return <NewPasswordStep onClose={onClose} onDone={onDone} onExpired={expire} />;
  if (dispatch) {
    return (
      <OtpStepModal
        dispatch={dispatch}
        verify={(referenceId, otp) => verify.mutateAsync({ referenceId, otp })}
        resend={(referenceId) => resend.mutateAsync(referenceId)}
        onVerified={() => setVerified(true)}
        onExpired={expire}
        onClose={onClose}
      />
    );
  }
  return (
    <Modal
      open
      size="sm"
      onClose={onClose}
      title={CHANGE_PASSWORD_COPY.STEP1_TITLE}
      subtitle={CHANGE_PASSWORD_COPY.STEP1_SUBTITLE}
      primaryText="Send Code"
      // A failure is toasted by <MutationToaster>; staying on Step 1 is just not advancing.
      onPrimary={() => send.mutate(undefined, { onSuccess: setDispatch })}
      loading={send.isPending}
    >
      <TextField name="phone" control={control} label="Phone Number" prefix="+1" disabled />
    </Modal>
  );
}
